'use client';

import React, { useState, useEffect } from 'react';
import { cx } from 'cva';
import Image from 'next/image';
import useStore from '@/stores/store';
import { size, some } from 'lodash-es';
import { useRouter } from 'next/navigation';
import AppLink from '@/components/next-ui/Link';
import Typography from '@/components/next-ui/Typography';
import Button from '@/components/next-ui/Button';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import Chip from '@/components/next-ui/Chip';
import { API_BASE_URL } from '@/utils/constants';
import useHasUserSession from '@/hooks/useHasUserSession';
import FollowNonProfitButton from '@/components/Buttons/FollowNonProfitButton';
import useJoinNonProfitDialogStore from '@/components/store/joinNonProfitDialogSlice';
import useNonProfitSignUpPopUpStore from '@/components/store/nonProfitSignUpPopUpSlice';
import { authorizedServerFetch } from '@/actions/server.actions';
import Card from '@/components/next-ui/Card';
import { getUserCountry } from '@/utils/user.helper';

function NonProfitExploreCard(props) {
  const { nonProfit, className = '' } = props;
  const { avatar, name, city, state, country, causes = [] } = nonProfit;

  const imageUrl = avatar ? getQualifiedImageUrl(avatar) : '';
  const isNekiHostedImage = imageUrl.startsWith(API_BASE_URL);

  const [hasImage, setHasImage] = React.useState(imageUrl.length > 0);

  const hasCauses = size(causes) > 0;

  const remainingCauses = size(causes) >= 3 ? false : true;

  const address = [city, state, country].filter(Boolean).join(', ');
  const slug = nonProfit.slug || undefined;

  const router = useRouter();
  const hasUserSession = useHasUserSession();
  const setShowJoinNonProfitModal = useJoinNonProfitDialogStore(
    (state) => state.setOpen,
  );

  const setShowNonProfitSignUpPopUp = useNonProfitSignUpPopUpStore(
    (state) => state.setOpen,
  );

  const user = useStore((state) => state.user?.profile);
  const userCommunities = user?.communities;

  const isUserMemberOfNonProfit = some(userCommunities, {
    communityId: nonProfit.company_id,
  });

  const defaultCountry = getUserCountry(user);

  useEffect(() => {
    setHasImage(imageUrl.length > 0);
  }, [imageUrl]);

  const handleImageError = (e) => {
    console.log(`Error loading image: ${imageUrl}`);
    setHasImage(false);
  };

  const renderText = () => {
    let nonProfitCardText = null;
    if (isUserMemberOfNonProfit) {
      return 'View';
    }
    if (nonProfit?.isClaimed) {
      nonProfitCardText = 'Join';
    } else {
      nonProfitCardText = 'View';
    }
    return nonProfitCardText;
  };

  const submitPublicNonProfitApplication = async () => {
    const res = await authorizedServerFetch(
      `users/me/nonprofit/${nonProfit.id}/apply`,
      {
        method: 'POST',
        body: JSON.stringify({
          message: '',
        }),
      },
    );

    if (res?.ok) {
      setShowJoinNonProfitModal(true, nonProfit, 'success');
    } else {
      if (res?.status === 409) {
        setShowJoinNonProfitModal(true, nonProfit, 'error');
      } else {
        setShowJoinNonProfitModal(true, nonProfit, 'backend-error');
      }
    }
  };

  const handleCardClick = (e) => {
    e.preventDefault(); // Prevent the link from being triggered
    e.stopPropagation(); // Prevent the event from bubbling up
    if (hasUserSession) {
      if (isUserMemberOfNonProfit) {
        router.push(slug);
      } else if (!nonProfit?.isClaimed) {
        router.push(slug);
      } else if (nonProfit?.company?.visibilityType === 'public') {
        submitPublicNonProfitApplication();
      } else {
        setShowJoinNonProfitModal(true, nonProfit);
      }
    } else {
      if (nonProfit?.isClaimed) {
        const loggedOutNonprofit = { ...nonProfit, logo_url: avatar };
        setShowNonProfitSignUpPopUp(true, loggedOutNonprofit);
      } else {
        router.push(slug);
      }
    }
    // router.push(slug);
  };

  return (
    <Card
      padding="none"
      edgeSize="lg"
      className={cx(className, 'rounded-[0.625rem] p-4 drop-shadow-card')}>
      <div className={`group/item overflow-hidden`}>
        {/* Image Section */}

        <div className="relative">
          <AppLink href={slug}>
            {hasImage && (
              <img
                src={imageUrl}
                alt={name}
                className="h-[250px] w-full rounded-xl border-white object-contain"
                onError={handleImageError}
              />
            )}

            {!hasImage && (
              <div className="flex h-[250px] w-full items-center justify-center rounded-xl bg-secondary-original-light-1 text-center">
                <p className="text-100 text-chips-original-secondary">
                  {name[0]}
                </p>
              </div>
            )}
            {/* {isNekiHostedImage ? (
              <img
                src={getQualifiedImageUrl(avatar)}
                alt={name}
                className="h-[250px] w-full rounded-xl border-white object-contain"
              />
            ) : (
              <div className="flex h-[250px] w-full items-center justify-center rounded-xl bg-secondary-original-light-1 text-center">
                <p className="text-100 text-chips-original-secondary">
                  {name[0]}
                </p>
              </div>
            )} */}
          </AppLink>
          {hasUserSession && (
            <div className="absolute right-4 top-4  flex items-center justify-center rounded-full bg-white">
              <FollowNonProfitButton
                size="md"
                nonProfit={nonProfit}
                noConfirmation
                className=" text-text-secondary"
              />
            </div>
          )}

          <Button
            disabled
            variant="filled"
            className="absolute left-4 top-4 bg-[#FFF5E0] text-13 text-secondary-ORIGINAL">
            {defaultCountry === 'IN' ? 'NGO' : 'Nonprofit'}
          </Button>

          <Button
            variant="filled"
            className={`absolute bottom-4 left-4 bg-body-background text-13 text-text-primary opacity-0 group-hover/item:opacity-90`}
            onClick={handleCardClick}>
            {renderText()}
          </Button>
        </div>

        {/* Content Section */}
        <AppLink href={slug}>
          <div className={cx('flex h-[188px] flex-col gap-2 pt-4')}>
            {/* Title */}
            <Typography
              variant="h5"
              className="font-medum line-clamp-1 hover:text-primary-ORIGINAL">
              {name}
            </Typography>

            {/* Location */}

            <div className="flex flex-row gap-2">
              <Image
                className="text-text-secondary"
                src="/assets/icons/location.svg"
                alt="location"
                width={16}
                height={16}
              />
              <Typography
                variant="body2"
                color="textSecondary"
                className="line-clamp-1">
                {address}
              </Typography>
            </div>

            {hasCauses && (
              <div className="flex flex-row gap-2">
                <Image
                  className="text-text-secondary"
                  src="/assets/icons/causes-supported.svg"
                  alt="causes-supported"
                  width={16}
                  height={16}
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="line-clamp-1">
                  Causes Supported
                </Typography>
              </div>
            )}

            {/* Causes Supported */}
            <div className="flex max-h-[85px] flex-wrap gap-2 overflow-hidden">
              {causes.slice(0, 2).map((cause) => (
                <Chip
                  text="small"
                  key={cause.id}
                  className="max-w-[200px] truncate !bg-chips-original-secondary">
                  {cause.cause_title}
                </Chip>
              ))}
              <Typography variant="h3">
                {causes.length > 2 ? '...' : ''}
              </Typography>
            </div>
          </div>
        </AppLink>
      </div>
    </Card>
  );
}

export default React.memo(NonProfitExploreCard);
